import { openDB, type DBSchema, type IDBPDatabase } from 'idb'
import type {
  AppNotification,
  SongMedia,
  ApiKey,
  AppSettings,
  Arrangement,
  Blockout,
  Ministry,
  Person,
  Plan,
  PlanItem,
  Position,
  SchedulingAssignment,
  ServiceType,
  Song,
  SyncQueueItem,
  Team,
  TeamMembership,
} from '@/types'

export const DB_NAME = 'mymdv-services'
export const DB_VERSION = 2

/**
 * IndexedDB schema. Every store mirrors a REST collection one-for-one, so the
 * mock API layer is a thin translation and a real backend can be swapped in
 * without changing a single store name.
 */
export interface MyMDVDB extends DBSchema {
  ministries: { key: string; value: Ministry; indexes: { 'by-sortOrder': number } }
  teams: {
    key: string
    value: Team
    indexes: { 'by-ministry': string; 'by-sortOrder': number }
  }
  positions: {
    key: string
    value: Position
    indexes: { 'by-team': string; 'by-sortOrder': number }
  }
  people: {
    key: string
    value: Person
    indexes: { 'by-lastName': string; 'by-email': string; 'by-role': string }
  }
  memberships: {
    key: string
    value: TeamMembership
    indexes: { 'by-person': string; 'by-team': string }
  }
  serviceTypes: { key: string; value: ServiceType; indexes: { 'by-ministry': string } }
  plans: {
    key: string
    value: Plan
    indexes: { 'by-date': string; 'by-serviceType': string; 'by-status': string }
  }
  planItems: {
    key: string
    value: PlanItem
    indexes: { 'by-plan': string; 'by-song': string }
  }
  songs: {
    key: string
    value: Song
    /**
     * No index on `isPublic`: IndexedDB cannot index booleans, and the song
     * library is small enough that filtering in memory is cheaper than
     * maintaining a denormalised 0/1 mirror column.
     */
    indexes: { 'by-title': string; 'by-artist': string }
  }
  arrangements: { key: string; value: Arrangement; indexes: { 'by-song': string } }
  songMedia: {
    key: string
    value: SongMedia
    indexes: { 'by-song': string; 'by-arrangement': string }
  }
  /**
   * The audio itself, kept apart from the metadata so listing a song's tracks
   * never drags megabytes of Blob through a transaction.
   */
  mediaBlobs: { key: string; value: { id: string; blob: Blob } }
  assignments: {
    key: string
    value: SchedulingAssignment
    indexes: {
      'by-plan': string
      'by-person': string
      'by-team': string
      'by-status': string
    }
  }
  blockouts: { key: string; value: Blockout; indexes: { 'by-person': string } }
  notifications: {
    key: string
    value: AppNotification
    indexes: { 'by-person': string; 'by-createdAt': string }
  }
  apiKeys: { key: string; value: ApiKey }
  settings: { key: string; value: AppSettings }
  syncQueue: {
    key: string
    value: SyncQueueItem
    indexes: { 'by-status': string; 'by-createdAt': string }
  }
}

export type ServicesDB = IDBPDatabase<MyMDVDB>

let dbPromise: Promise<ServicesDB> | null = null

/**
 * Opens (and lazily creates) the database. Safe to call from anywhere — the
 * promise is memoised so concurrent callers share one connection.
 */
export function openDatabase(): Promise<ServicesDB> {
  if (dbPromise) return dbPromise

  dbPromise = openDB<MyMDVDB>(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion) {
      // v1 — initial schema.
      if (oldVersion < 1) {
        const ministries = db.createObjectStore('ministries', { keyPath: 'id' })
        ministries.createIndex('by-sortOrder', 'sortOrder')

        const teams = db.createObjectStore('teams', { keyPath: 'id' })
        teams.createIndex('by-ministry', 'ministryId')
        teams.createIndex('by-sortOrder', 'sortOrder')

        const positions = db.createObjectStore('positions', { keyPath: 'id' })
        positions.createIndex('by-team', 'teamId')
        positions.createIndex('by-sortOrder', 'sortOrder')

        const people = db.createObjectStore('people', { keyPath: 'id' })
        people.createIndex('by-lastName', 'lastName')
        people.createIndex('by-email', 'email', { unique: true })
        people.createIndex('by-role', 'role')

        const memberships = db.createObjectStore('memberships', { keyPath: 'id' })
        memberships.createIndex('by-person', 'personId')
        memberships.createIndex('by-team', 'teamId')

        const serviceTypes = db.createObjectStore('serviceTypes', { keyPath: 'id' })
        serviceTypes.createIndex('by-ministry', 'ministryId')

        const plans = db.createObjectStore('plans', { keyPath: 'id' })
        plans.createIndex('by-date', 'serviceDate')
        plans.createIndex('by-serviceType', 'serviceTypeId')
        plans.createIndex('by-status', 'status')

        const planItems = db.createObjectStore('planItems', { keyPath: 'id' })
        planItems.createIndex('by-plan', 'planId')
        planItems.createIndex('by-song', 'songId')

        const songs = db.createObjectStore('songs', { keyPath: 'id' })
        songs.createIndex('by-title', 'title')
        songs.createIndex('by-artist', 'artist')

        const arrangements = db.createObjectStore('arrangements', { keyPath: 'id' })
        arrangements.createIndex('by-song', 'songId')

        const assignments = db.createObjectStore('assignments', { keyPath: 'id' })
        assignments.createIndex('by-plan', 'planId')
        assignments.createIndex('by-person', 'personId')
        assignments.createIndex('by-team', 'teamId')
        assignments.createIndex('by-status', 'status')

        const blockouts = db.createObjectStore('blockouts', { keyPath: 'id' })
        blockouts.createIndex('by-person', 'personId')

        const notifications = db.createObjectStore('notifications', { keyPath: 'id' })
        notifications.createIndex('by-person', 'personId')
        notifications.createIndex('by-createdAt', 'createdAt')

        db.createObjectStore('apiKeys', { keyPath: 'id' })
        db.createObjectStore('settings', { keyPath: 'id' })

        const syncQueue = db.createObjectStore('syncQueue', { keyPath: 'id' })
        syncQueue.createIndex('by-status', 'status')
        syncQueue.createIndex('by-createdAt', 'createdAt')
      }

      // v2 — audio attachments for rehearsal.
      if (oldVersion < 2) {
        const songMedia = db.createObjectStore('songMedia', { keyPath: 'id' })
        songMedia.createIndex('by-song', 'songId')
        songMedia.createIndex('by-arrangement', 'arrangementId')
        db.createObjectStore('mediaBlobs', { keyPath: 'id' })
      }
    },
    blocked() {
      console.warn('[db] Upgrade blocked — another tab still holds the old version.')
    },
    blocking() {
      console.warn('[db] This tab is blocking an upgrade; closing the connection.')
      void dbPromise?.then((db) => db.close())
      dbPromise = null
    },
    terminated() {
      console.error('[db] Connection terminated unexpectedly; will reopen on demand.')
      dbPromise = null
    },
  })

  return dbPromise
}

/** Drops the whole database — used by Admin → Danger zone → "Reset demo data". */
export async function deleteDatabase(): Promise<void> {
  const db = await openDatabase()
  db.close()
  dbPromise = null
  await new Promise<void>((resolve, reject) => {
    const request = indexedDB.deleteDatabase(DB_NAME)
    request.onsuccess = () => resolve()
    request.onerror = () => reject(request.error)
    request.onblocked = () => resolve()
  })
}
